import Router, { NextRouter } from "next/router";
import type { UrlObject } from "url";
import type UrlsService from "./urlsService";
import { ParsedUrlQuery } from "querystring";

export type INavigateOptions = Partial<{
    shallow: boolean;
    scroll: boolean;
    query: ParsedUrlQuery;
}>;

export default class NavigationService {
    private readonly urlsService: UrlsService;
    private history = [];

    constructor(urlService: UrlsService) {
        this.urlsService = urlService;

        if (typeof window !== "undefined") {
            Router.events.on("routeChangeStart", (_url: string, _options: INavigateOptions) => {
                this.history.push(Router.asPath);
            });

            Router.events.on("routeChangeComplete", (_url: string, _options: INavigateOptions) => {
                const isNotLocal = window.location.hostname !== "local.www.tiendeo.com";
                const isBehindBalancer = window.location.hostname.includes("www.tiendeo");

                if (window && window.location && isNotLocal && isBehindBalancer) {
                    history.replaceState(
                        {
                            ...history.state,
                            as: history.state.as.replace("/profile-webapp", ""),
                        },
                        document.title,
                        window.location.href.replace("/profile-webapp", ""),
                    );
                }
            });
        }
    }

    public Landing = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({}),
            this.urlsService.Landing({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public EditProfile = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({ isEditProfileModalOpen: true }),
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public MyMedalsWebview = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({ isMyMedalsModalOpen: true }),
            this.urlsService.Landing({
                isMyMedalsModalOpen: true,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public VerifyPhone = (phone: string, options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isVerifyPhoneModalVisible: true,
                phone: phone,
            }),
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isVerifyPhoneModalVisible: true,
                phone: phone,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public AskVerifyPhone = (phone: string, options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isAskVerifyPhoneModalVisible: true,
                phone: phone,
            }),
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isAskVerifyPhoneModalVisible: true,
                phone: phone,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public ConfirmChangeVerifiedPhone = (phone: string, options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isConfirmChangeVerifiedPhoneModalVisible: true,
                phone: phone,
            }),
            this.urlsService.Landing({
                isEditProfileModalOpen: true,
                isConfirmChangeVerifiedPhoneModalVisible: true,
                phone: phone,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public OpenProfileMenu = (
        asPath: string,
        pathname: NextRouter["pathname"],
        query: NextRouter["query"],
        options?: INavigateOptions,
    ) =>
        this.Navigate(
            this.urlsService.OpenMenu(asPath, pathname, query, {}),
            this.urlsService.OpenMenu(asPath, pathname, query, {
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public CloseProfileMenu = (
        asPath: string,
        pathname: NextRouter["pathname"],
        query: NextRouter["query"],
        options?: INavigateOptions,
    ) =>
        this.Navigate(
            this.urlsService.CloseMenu(asPath, pathname, query, {}),
            this.urlsService.CloseMenu(asPath, pathname, query, {
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public Login = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Login({}),
            this.urlsService.Login({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public MyTiendeo = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.MyTiendeo({}),
            this.urlsService.MyTiendeo({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public LoyaltyCards = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.LoyaltyCards({}),
            this.urlsService.LoyaltyCards({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public MyMedals = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.MyMedals({}),
            this.urlsService.MyMedals({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public SavedPages = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.SavedPages({}),
            this.urlsService.SavedPages({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public Notifications = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Notifications({}),
            this.urlsService.Notifications({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public NotificationsManagement = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.NotificationsManagement({}),
            this.urlsService.NotificationsManagement({
                options: {
                    seo: true,
                    query: options.query,
                },
            }),
            options,
        );

    public Help = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Help({}),
            this.urlsService.Help({
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public ContactUs = (options?: INavigateOptions) =>
        this.Navigate(
            this.urlsService.Help({
                isContactUsOpen: true,
            }),
            this.urlsService.Help({
                isContactUsOpen: true,
                options: {
                    seo: true,
                },
            }),
            options,
        );

    public Back = (options: INavigateOptions = {}) => {
        const hasPreviousPage = this.HasPreviousPage();

        if (hasPreviousPage) {
            Router.back();
            this.history.pop();
            return;
        }

        this.Navigate(
            this.urlsService.Landing({}),
            this.urlsService.Landing({
                options: {
                    seo: true,
                },
            }),
            options,
        );
    };

    public HasPreviousPage = () => this.history.length > 0;

    private Navigate = (url: UrlObject, as: UrlObject, options: INavigateOptions = {}) =>
        Router.push(url, as, options);
}
